/**
 * The confirmation round (3.3). Nothing is drafted and nothing is reviewed.
 *
 * At the time the branch sets — a config value beside `confirmationChannel`,
 * 1:00 by default — every patient with a real visit on the clinician's next
 * working day and text consent gets **one** message carrying the time the
 * schedule already has. The visit reads Pending. A yes confirms it. Everything
 * else — the patient's own words, silence past the cutoff, no text consent, a
 * move after the text went out — lands on the clinician as one list, **Needs a
 * call**, which she clears by phone and a tap on **Confirm**.
 *
 * The clinician's only control over the round is **Delay**, which pushes today's
 * round back an hour, up to two, for her own round and for today only.
 *
 * Pure. Rows read the effective calendar, so a visit that slid in from today is
 * in the list the moment the card lands, before the server confirms it.
 *
 * **Reasons accumulate and never repeat.** A row that already carries the
 * patient's words keeps them and gains *Moved after the text*; a visit moved
 * twice still reads the move once. `addReason` is the only way a reason is
 * written, and it is add-if-absent by kind.
 *
 * **A move is read two ways, and they agree.** `deriveConfirmationResets`
 * (`confirmation.ts`) writes the move onto the annotation when a snapshot shows
 * a texted visit at a new hour or on a new clinician, and stamps `lastSeen*`
 * with where it now is. The row derivation *also* compares the visit in front of
 * it against `lastSeen*`, so a re-time still staged on My day — not written
 * anywhere yet — reads Needs a call at once, and **Reset** puts it back. Once
 * the save lands the stored reason carries it and the comparison is equal again:
 * the same row label throughout, written once.
 */
import type { ConfirmationReason, ConfirmationRound, DateKey, DemoState, IsoString, RoundStatus, VisitAnnotation, VisitResourceLike, VisitRound } from "@caredna-marlin/utils/homeHealthDemo/state";
import { addWorkingDaysToKey, formatInBranchZone, selectDemoNow, toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { effectiveVisitResources, isPendingId, selectEffectiveCalendar } from "./effectiveCalendar";
import { atWall, joinSentences, type TimeSentence } from "@caredna-marlin/utils/homeHealthDemo/engine/timeText";

/** The branch's defaults, used when a config predates the round. */
export const DEFAULT_SEND_TIME = "13:00";
export const DEFAULT_CUTOFF_TIME = "17:00";
/** The most hours a clinician can push her own round back, one hour at a time. */
export const MAX_DELAY_HOURS = 2;

function addHours(hhmm: string, hours: number): string {
  const [h, m] = hhmm.split(":").map(Number);
  return `${String(Math.min(23, h + hours)).padStart(2, "0")}:${String(m || 0).padStart(2, "0")}`;
}

export function sendTime(state: Pick<DemoState, "config">): string {
  return state.config.confirmationSendTime ?? DEFAULT_SEND_TIME;
}

export function cutoffTime(state: Pick<DemoState, "config">): string {
  return state.config.confirmationCutoffTime ?? DEFAULT_CUTOFF_TIME;
}

// ---------------------------------------------------------------------------
// the round record
// ---------------------------------------------------------------------------

/** One round: one clinician, one day. */
export function roundKey(providerId: number, forDate: DateKey): string {
  return `${providerId}:${forDate}`;
}

export const EMPTY_ROUND: ConfirmationRound = { delayHours: 0 };

export function roundFor(state: Pick<DemoState, "confirmationRounds">, providerId: number | undefined, forDate: DateKey): ConfirmationRound {
  if (providerId === undefined) return EMPTY_ROUND;
  return state.confirmationRounds?.[roundKey(providerId, forDate)] ?? EMPTY_ROUND;
}

export function roundFired(round: ConfirmationRound): boolean {
  return !!round.firedAtIso;
}

/** The hour today's round goes, after any delay. */
export function roundSendTime(state: Pick<DemoState, "config">, round: ConfirmationRound): string {
  return addHours(sendTime(state), round.delayHours ?? 0);
}

/**
 * **Delay**: an hour later, up to two, for this clinician's round and today
 * only. It changes the caption and nothing else, and it is gone once the round
 * has gone.
 */
export function delayRound(rounds: Record<string, ConfirmationRound>, providerId: number, forDate: DateKey): Record<string, ConfirmationRound> {
  const key = roundKey(providerId, forDate);
  const round = rounds[key] ?? EMPTY_ROUND;
  if (roundFired(round) || round.delayHours >= MAX_DELAY_HOURS) return rounds;
  return { ...rounds, [key]: { ...round, delayHours: round.delayHours + 1 } };
}

// ---------------------------------------------------------------------------
// consent and replies
// ---------------------------------------------------------------------------

/**
 * A yes is the affirmative set, not the letter Y. Trimmed, lower-cased, stripped
 * of the punctuation a phone adds. Anything else is a non-yes and goes to Needs
 * a call with the patient's own words on the row.
 */
const AFFIRMATIVE = new Set(["y", "ya", "yes", "yep", "yeah", "yup", "ok", "okay", "k", "sure", "confirm", "confirmed", "correct", "right", "fine", "good", "great", "perfect", "works", "that works", "sounds good", "see you then", "thanks", "thank you", "yes thanks", "ok thanks", "yes please"]);

export function isAffirmative(reply: string): boolean {
  const cleaned = reply
    .trim()
    .toLowerCase()
    .replace(/[.!,;:"'’]+$/g, "")
    .replace(/\s+/g, " ");
  return AFFIRMATIVE.has(cleaned);
}

/**
 * Consent is the `smsConsent` constraint from the welcome call, and the default
 * is the wizard's own: a patient counts as consented unless the constraint is
 * explicitly "no".
 */
export function hasTextConsent(state: Pick<DemoState, "meta" | "patients">, patientId: number): boolean {
  const ref = Object.entries(state.meta.bindings.patientIdsByRef).find(([, id]) => id === patientId)?.[0];
  const constraint = ref ? state.patients[ref]?.constraints.find((c) => c.kind === "smsConsent") : undefined;
  return constraint?.value.trim().toLowerCase() !== "no";
}

// ---------------------------------------------------------------------------
// reasons
// ---------------------------------------------------------------------------

/**
 * Add-if-absent, by kind. This is what makes the move idempotent: a visit moved
 * twice reads *Needs a call · "afternoon?" · Moved after the text*, not the move
 * twice over. The patient's words are never replaced by a later move either —
 * the reason that was already there keeps its place and its text.
 */
export function addReason(reasons: ConfirmationReason[] | undefined, reason: ConfirmationReason): ConfirmationReason[] {
  const list = reasons ?? [];
  if (list.some((r) => r.kind === reason.kind)) return list;
  return [...list, reason];
}

export function reasonLabel(state: Pick<DemoState, "config">, reason: ConfirmationReason): TimeSentence {
  switch (reason.kind) {
    case "words":
      return `“${reason.text ?? ""}”`;
    case "noConsent":
      return "No text consent";
    case "moved":
      return "Moved after the text";
  }
}

/** Words first, the move last, whatever order they arrived in. */
const REASON_ORDER: ConfirmationReason["kind"][] = ["words", "noConsent", "moved"];

export function reasonLabels(state: Pick<DemoState, "config">, reasons: ConfirmationReason[] | undefined): TimeSentence[] {
  return [...(reasons ?? [])].sort((a, b) => REASON_ORDER.indexOf(a.kind) - REASON_ORDER.indexOf(b.kind)).map((r) => reasonLabel(state, r));
}

// ---------------------------------------------------------------------------
// the four states
// ---------------------------------------------------------------------------

export const STATUS_LABEL: Record<RoundStatus, string> = {
  notSent: "Not sent",
  pending: "Pending",
  confirmed: "Confirmed",
  needsCall: "Needs a call",
};

/**
 * **A waiting row says when it was texted, not that it is waiting** (Pete,
 * 2026-09-22): *Sent 1:00PM* rather than *Pending*. The hour is what somebody
 * looking at an unanswered row actually wants — how long it has been out — and
 * the icon already says the state. It is handed back as a wall-clock token so
 * the view phrases it under the user's own setting. A round with no send time
 * recorded falls back to the plain word.
 */
function statusLabel(state: Pick<DemoState, "config">, round: VisitRound): TimeSentence {
  if (round.status !== "pending" || !round.textedAtIso) return STATUS_LABEL[round.status];
  const tz = state.config.branch.timeZone;
  return ["Sent ", atWall(formatInBranchZone(new Date(round.textedAtIso), tz, "HH:mm"))];
}

/** The whole row label: the state, then the reasons it carries. */
export function roundLabel(state: Pick<DemoState, "config">, round: VisitRound | undefined): TimeSentence {
  if (!round) return STATUS_LABEL.notSent;
  const parts: TimeSentence[] = [statusLabel(state, round), ...reasonLabels(state, round.reasons)];
  return joinSentences(parts, " · ");
}

// ---------------------------------------------------------------------------
// rows
// ---------------------------------------------------------------------------

export interface ConfirmationRow {
  realVisitId: number;
  patientName: string;
  startIso: IsoString;
  endIso: IsoString;
  visitTypeName: string;
  status: RoundStatus;
  reasons: ConfirmationReason[];
  /** The whole label as the panel, the chip and the block all read it. Phrased by the view. */
  label: TimeSentence;
  /** The reasons alone, in order, for the caption under the row. Phrased by the view. */
  reasonLabels: TimeSentence[];
  /** The clinician confirmed this one by phone rather than the patient by text. */
  confirmedByPhone: boolean;
  /** The move is optimistic: the server has not confirmed it yet. */
  pending: boolean;
}

/**
 * Where a visit with no round record of its own stands: waiting for the round
 * before it goes; after it, Not sent — a visit that lands once the texts have
 * gone is never texted on its own (Pete, 2026-09-25) — or Needs a call when the
 * patient has no text consent.
 */
function impliedRound(state: DemoState, vr: VisitResourceLike, round: ConfirmationRound): VisitRound {
  if (roundFired(round) && !hasTextConsent(state, vr.patientId)) return { status: "needsCall", reasons: [{ kind: "noConsent" }] };
  return { status: "notSent", reasons: [] };
}

/**
 * How far a visit may shuffle inside the window it was texted before the patient
 * has to be told again, as a fraction of that window's width (Pete, 2026-09-19:
 * *"minor shuffling within the arrival window doesn't need to reset the confirmed
 * status (say +/-50% of the window)"*). A 60-minute window buys ±30 minutes.
 */
export const MOVE_TOLERANCE_OF_WINDOW = 0.5;

/**
 * What a text carried: where the visit stood and what window it named. A visit
 * with **no** arrival window gets no window anchors, which leaves the tolerance
 * at zero — nothing was promised beyond the minute, so any change at all is a
 * move. Standing the visit's own span in for a window would quietly buy it half
 * its duration of slack that nobody ever told the patient about.
 */
export function anchorsFor(vr: Pick<VisitResourceLike, "providerId" | "startIso" | "endIso" | "arrivalWindowStartIso" | "arrivalWindowEndIso">): Pick<VisitRound, "textedProviderId" | "textedStartIso" | "textedWindowStartIso" | "textedWindowEndIso"> {
  return {
    textedProviderId: vr.providerId,
    textedStartIso: vr.startIso,
    ...(vr.arrivalWindowStartIso && vr.arrivalWindowEndIso ? { textedWindowStartIso: vr.arrivalWindowStartIso, textedWindowEndIso: vr.arrivalWindowEndIso } : {}),
  };
}

function minutesBetween(a: string, b: string): number {
  return Math.round(Math.abs(new Date(a).getTime() - new Date(b).getTime()) / 60_000);
}

/**
 * The move, read off the visit in front of us rather than off a snapshot, and
 * measured against **what the patient was told** rather than against wherever
 * the visit drifted to last. This is what makes a staged re-time on My day read
 * Needs a call before Save, and what makes **Reset** put the row back.
 *
 * The rule, exactly. A change of clinician is always a move. A change of start
 * is a move when
 *
 *     |start − textedStart| > MOVE_TOLERANCE_OF_WINDOW × (textedWindowEnd − textedWindowStart)
 *
 * in whole minutes; at or inside that it is minor shuffling inside the window the
 * patient already has, and nobody needs telling. The anchors are fixed at text
 * time and a snapshot never updates them, so two small moves the same way add up
 * and eventually cross the line, rather than each being measured from the last.
 * A round with no anchors — a store written before they existed — treats any
 * change as a move, which is the safe answer.
 */
export function movedAfterText(annotation: VisitAnnotation | undefined, vr: Pick<VisitResourceLike, "providerId" | "startIso">): boolean {
  const round = annotation?.round;
  if (!round || round.status === "notSent") return false;
  if (round.textedProviderId !== undefined && vr.providerId !== round.textedProviderId) return true;
  if (round.textedStartIso === undefined) {
    // No anchor to measure against: fall back to "anything at all is a move".
    return annotation.lastSeenStartIso !== undefined && vr.startIso !== annotation.lastSeenStartIso;
  }
  if (vr.startIso === round.textedStartIso) return false;
  const windowMinutes = round.textedWindowStartIso && round.textedWindowEndIso ? minutesBetween(round.textedWindowStartIso, round.textedWindowEndIso) : 0;
  return minutesBetween(vr.startIso, round.textedStartIso) > Math.round(windowMinutes * MOVE_TOLERANCE_OF_WINDOW);
}



/**
 * **A visit that has moved to another day was told about a day it is no longer
 * on** (Pete, 2026-09-25: *"moving somebody to another day should clear their
 * notification status"*). Nothing the patient was asked still applies — not the
 * yes, not the request for the afternoon — so the round record is dropped rather
 * than carried across, and the card goes back to saying nothing about texts. A
 * move *within* the day is a different thing: the patient still has the right
 * day and the wrong hour, which is `movedAfterText` and *Moved after the text*.
 *
 * Measured against what the patient was actually told, falling back to where the
 * visit was last seen when there is no anchor.
 */
export function movedToAnotherDay(annotation: VisitAnnotation | undefined, vr: Pick<VisitResourceLike, "startIso">, timeZone: string): boolean {
  const round = annotation?.round;
  if (!round || round.status === "notSent") return false;
  const told = round.textedStartIso ?? annotation?.lastSeenStartIso;
  if (told === undefined) return false;
  return toDateKey(new Date(vr.startIso), timeZone) !== toDateKey(new Date(told), timeZone);
}

/** A texted visit that has moved is Needs a call, and keeps every reason it had. */
export function withMove(round: VisitRound): VisitRound {
  if (round.status === "notSent") return round;
  return { ...round, status: "needsCall", reasons: addReason(round.reasons, { kind: "moved" }), confirmedAtIso: undefined, confirmedByPhone: false };
}

/** The round's rows for one clinician's day, in time order — one per card on that day. */
export function selectConfirmationRows(state: DemoState, providerId: number | undefined, forDate: DateKey): ConfirmationRow[] {
  if (providerId === undefined) return [];
  const round = roundFor(state, providerId, forDate);
  const { visits } = selectEffectiveCalendar(state, providerId, { start: forDate, end: forDate });
  return visits.map((vr) => {
    const annotation = state.annotations[String(vr.visitId)];
    const stored = annotation?.round;
    const base = stored ?? impliedRound(state, vr, round);
    const visitRound = stored && movedAfterText(annotation, vr) ? withMove(stored) : base;
    return {
      realVisitId: vr.visitId,
      patientName: vr.patientName,
      startIso: vr.startIso,
      endIso: vr.endIso,
      visitTypeName: vr.visitTypeName,
      status: visitRound.status,
      reasons: visitRound.reasons,
      label: roundLabel(state, visitRound),
      reasonLabels: reasonLabels(state, visitRound.reasons),
      confirmedByPhone: !!visitRound.confirmedByPhone,
      pending: isPendingId(vr.visitId),
    };
  });
}

// ---------------------------------------------------------------------------
// what the round does
// ---------------------------------------------------------------------------

function annotationWith(annotations: Record<string, VisitAnnotation>, vr: VisitResourceLike, round: VisitRound): VisitAnnotation {
  const existing = annotations[String(vr.visitId)];
  return {
    ...(existing ?? { realVisitId: vr.visitId, origin: "census" as const, confirmation: "notSent" as const }),
    // A row that is only waiting for the round has said nothing to anybody yet, so
    // it carries no anchors; everything texted carries what it told them.
    round: round.status === "notSent" ? round : { ...anchorsFor(vr), ...round },
    // Where the text went out from: anything that moves the visit off this is a move.
    lastSeenProviderId: vr.providerId,
    lastSeenStartIso: vr.startIso,
  };
}

/**
 * **The round goes.** One text per real scheduled visit on the day whose patient
 * has text consent; a patient without it is Needs a call from the start.
 * Placeholders are never texted, and neither is anybody who already has a round
 * record: nobody is texted twice, ever.
 */
export function fireRound(state: DemoState, providerId: number | undefined, forDate: DateKey, nowIso: IsoString): { annotations: Record<string, VisitAnnotation>; textedVisitIds: number[] } {
  const annotations = { ...state.annotations };
  const textedVisitIds: number[] = [];
  if (providerId === undefined) return { annotations, textedVisitIds };
  const { visits } = selectEffectiveCalendar(state, providerId, { start: forDate, end: forDate });
  for (const vr of visits) {
    if (isPendingId(vr.visitId)) continue;
    // Anybody already texted is skipped — nobody hears from us twice.
    const existing = annotations[String(vr.visitId)]?.round;
    if (existing && existing.status !== "notSent") continue;
    if (!hasTextConsent(state, vr.patientId)) {
      annotations[String(vr.visitId)] = annotationWith(annotations, vr, { status: "needsCall", reasons: [{ kind: "noConsent" }] });
      continue;
    }
    annotations[String(vr.visitId)] = annotationWith(annotations, vr, { status: "pending", reasons: [], textedAtIso: nowIso });
    textedVisitIds.push(vr.visitId);
  }
  return { annotations, textedVisitIds };
}

export interface ScriptedReply {
  realVisitId: number;
  /** The patient's own words. Absent means they never answered. */
  text?: string;
}

/**
 * **The cutoff.** The scripted replies and the cutoff land together: a yes
 * confirms, and anything that is not a yes carries the patient's words to Needs
 * a call. A row that already needs a call keeps the reason it has.
 *
 * **One test, not two** (Pete, 2026-09-24). There used to be a Declined state
 * beside this one, reached by a flag the presenter set rather than by anything
 * read off the message. Nothing here could have told "No, tomorrow doesn't work"
 * from "can you come later?" — that is not keyword matching — so the two states
 * were a claim about parsing we cannot make. What survives is the claim we can:
 * `isAffirmative` knows a yes, and a human reads the rest.
 *
 * **Whoever never answered is left exactly as it was** (Pete, 2026-09-22): the
 * row goes on reading *Sent 1:00PM*, which is the truth about it. A silence used
 * to be swept into Needs a call with a *No reply by 5:00PM* reason, which made
 * the branch chase a patient who has done nothing wrong and put a second red
 * icon on the week beside the one that means somebody actually asked for
 * something. The cutoff still exists as the hour the round is scored at; it just
 * no longer turns silence into work.
 */
export function applyReplies(state: DemoState, providerId: number | undefined, forDate: DateKey, replies: ScriptedReply[], nowIso: IsoString): Record<string, VisitAnnotation> {
  if (providerId === undefined) return state.annotations;
  const annotations = { ...state.annotations };
  const byVisit = new Map(replies.map((r) => [r.realVisitId, r]));
  for (const row of selectConfirmationRows(state, providerId, forDate)) {
    const annotation = annotations[String(row.realVisitId)];
    const stored = annotation?.round;
    if (!stored || stored.status !== "pending") continue;
    const reply = byVisit.get(row.realVisitId);
    if (reply?.text === undefined) continue;
    const next: VisitRound = isAffirmative(reply.text)
      ? { ...stored, status: "confirmed", reasons: stored.reasons, repliedAtIso: nowIso, confirmedAtIso: nowIso }
      : { ...stored, status: "needsCall", reasons: addReason(stored.reasons, { kind: "words", text: reply.text.trim() }), repliedAtIso: nowIso };
    annotations[String(row.realVisitId)] = { ...annotation, round: next };
  }
  return annotations;
}

/**
 * **Confirm.** The clinician has called the patient. The row is Confirmed and
 * the reasons go with the call that resolved them — so a visit she moves again
 * afterwards comes back reading *Needs a call · Moved after the text* and
 * nothing else.
 */
export function confirmByPhone(state: DemoState, realVisitId: number, nowIso: IsoString): Record<string, VisitAnnotation> {
  const annotation = state.annotations[String(realVisitId)];
  const stored = annotation?.round;
  if (stored?.status === "confirmed") return state.annotations;
  // Where the visit is *now*, overlay and all — a save that has landed optimistically
  // but not yet come back on a poll is still where she and the patient just agreed it is.
  const vr = effectiveVisitResources(state).find((v) => v.visitId === realVisitId);
  // **A visit the round has not reached yet has nothing stored at all**: its *Not
  // sent* is implied by `selectConfirmationRows`, never written down. A patient
  // who rings ahead confirms it all the same, so the annotation and its round are
  // created here rather than requiring a text to have gone out first. Bailing
  // instead is what made **Mark confirmed** a no-op on a card before the round.
  if (!stored && !vr) return state.annotations;
  return {
    ...state.annotations,
    [String(realVisitId)]: {
      ...(annotation ?? { realVisitId, origin: "census" as const, confirmation: "notSent" as const }),
      // The call is a new telling, so it **re-anchors**: what she and the patient
      // just agreed is what a later move is measured against. Without this the
      // next poll would read the old anchors and take the confirmation away again.
      round: { ...(stored ?? { status: "notSent" as const, reasons: [] }), ...(vr ? anchorsFor(vr) : {}), status: "confirmed", reasons: [], confirmedAtIso: nowIso, confirmedByPhone: true },
      ...(vr ? { lastSeenProviderId: vr.providerId, lastSeenStartIso: vr.startIso } : {}),
    },
  };
}

/**
 * What the one patient who says no outright writes back (script author,
 * discussion turn 71). Flat, not apologetic, and refusing **the day** rather
 * than the hour, which is what makes it the branch's work to rebook rather than
 * the clinician's to reschedule.
 *
 * That distinction is for the person reading the row, not for the software: the
 * round files this with every other reply that is not a yes, because telling a
 * no from a counteroffer is not something keyword matching can do (Pete,
 * 2026-09-24). Which it is, is legible only in her own words, and that is
 * exactly where they sit.
 */
/** The scripted patient who says no. Her words go on the row; the row is Needs a call like any other reply that is not a yes. */
export const SAYS_NO_WORDS = "No, tomorrow doesn't work. I have a doctor's appointment.";

/**
 * Who says what when the presenter fires the 5:00 event, deterministically.
 * Every pending row says yes except:
 *
 *   - the one the script names, who asks for the afternoon → **Needs a call**
 *     with her own words;
 *   - the **last** routine row of the day that is neither the named one nor a
 *     slide-in, who never answers at all and is therefore left alone, still
 *     reading *Sent 1:00PM* (Pete, 2026-09-22);
 *   - `saysNoPatient`, when the caller names one, who says no outright → **Needs
 *     a call** like any other reply that is not a yes, carrying `SAYS_NO_WORDS`.
 *     The row is no less the branch's work for sharing a state with Whitfield's;
 *     what it is is on the row, in her own words.
 *
 * **The one who says no is named, not positional** (script author, discussion turn 71):
 * the first routine row of Dana's Thursday is Halvorsen, and 3.3's Say line
 * leans on Halvorsen being confirmed at his hour, so a positional pick would
 * break the beat. Naming her is also what makes the no opt-in: a caller
 * setting up a confirmed day names nobody and gets the round's ordinary
 * behaviour.
 */
export function scriptedReplies(rows: ConfirmationRow[], asksLaterPatient: string, words: string, saysNoPatient?: string): ScriptedReply[] {
  const pending = rows.filter((r) => r.status === "pending").sort((a, b) => a.startIso.localeCompare(b.startIso));
  const named = (r: ConfirmationRow, name: string) => r.patientName.toLowerCase().includes(name.toLowerCase());
  const isNamed = (r: ConfirmationRow) => named(r, asksLaterPatient);
  const eligible = pending.filter((r) => !isNamed(r));
  /**
   * **The name, and a place to stand when the name is not on the day.** The one
   * who says no is on Dana's Thursday; a round fired for any other day — a
   * rehearsal run before the seed's own date, a walk that starts on the wrong
   * morning — would find nobody of that name and quietly produce no no at all,
   * which is what Pete hit on 2026-09-22: *"I didn't get any declines back … 5
   * confirms and a no response."* A beat that silently does not happen is worse than one that
   * happens to a different patient, so a miss falls back to the middle of the
   * day: never the first row, which 3.3's Say line leans on, and never the last,
   * which is the one nobody answers.
   */
  const saysNo = (saysNoPatient ? eligible.find((r) => named(r, saysNoPatient)) : undefined) ?? (saysNoPatient && eligible.length >= 3 ? eligible[Math.floor((eligible.length - 1) / 2)] : undefined);
  const others = eligible.filter((r) => r.realVisitId !== saysNo?.realVisitId);
  const silent = others[others.length - 1];
  return pending.map((r) =>
    isNamed(r)
      ? { realVisitId: r.realVisitId, text: words }
      : r.realVisitId === saysNo?.realVisitId
        ? { realVisitId: r.realVisitId, text: SAYS_NO_WORDS }
        : r.realVisitId === silent?.realVisitId
          ? { realVisitId: r.realVisitId }
          : { realVisitId: r.realVisitId, text: "yes" },
  );
}

/**
 * The clinician's next working day — the day the round is about. On a Friday
 * that is Monday, so the beat has a day with visits in it whichever weekday the
 * demo runs on; the panel labels it by name rather than calling it "tomorrow".
 */
export function roundDateFor(state: Pick<DemoState, "config" | "meta">, now: Date): DateKey {
  return addWorkingDaysToKey(toDateKey(selectDemoNow(state, now), state.config.branch.timeZone), 1);
}
